import { useQuery } from '@apollo/client';
import { custom_field_entity_enum, custom_field_type_enum } from '@predium/enums';
import { CustomFieldBooleanFilter } from '@/components/building-filters/Filters/CustomFieldBooleanFilter';
import { CustomFieldNumberFilter } from '@/components/building-filters/Filters/CustomFieldNumberFilter';
import { CustomFieldStringFilter } from '@/components/building-filters/Filters/CustomFieldStringFilter';
import { useSession } from '@/components/session/useSession';
import { GET_CUSTOM_FIELD_VALUES, GET_CUSTOM_FIELDS_BY_ORG } from '@/graphql/DataCollection.queries';

type Props = {
  entity: custom_field_entity_enum;
};

export function CustomFieldFilters({ entity }: Props) {
  const {
    org: { id: orgId },
    featureFlags: { CUSTOM_PROPERTIES },
  } = useSession();

  const { data } = useQuery(GET_CUSTOM_FIELDS_BY_ORG, {
    variables: { orgId, entityType: entity },
    skip: !CUSTOM_PROPERTIES,
  });

  /* STRING fields filter by picking from the values actually in use, so the option
     lists come from the data rather than the definition */
  const { data: valuesData } = useQuery(GET_CUSTOM_FIELD_VALUES, {
    variables: { entity },
    skip: !CUSTOM_PROPERTIES,
  });

  const valuesByKey = new Map(
    (valuesData?.getCustomFieldValues.fields ?? []).map((field) => [
      field.key,
      { values: field.values, truncated: field.truncated },
    ]),
  );

  return (
    <>
      {(data?.custom_field ?? []).map((field) => {
        switch (field.type_id) {
          case custom_field_type_enum.STRING: {
            const fieldValues = valuesByKey.get(field.key);
            return (
              <CustomFieldStringFilter
                key={field.id}
                entity={entity}
                fieldKey={field.key}
                title={field.name}
                options={fieldValues?.values ?? []}
                truncated={fieldValues?.truncated ?? false}
              />
            );
          }
          case custom_field_type_enum.NUMBER:
            return (
              <CustomFieldNumberFilter
                key={field.id}
                entity={entity}
                fieldKey={field.key}
                title={field.name}
                suffix={field.suffix}
              />
            );
          case custom_field_type_enum.BOOLEAN:
            return <CustomFieldBooleanFilter key={field.id} entity={entity} fieldKey={field.key} title={field.name} />;
          default: {
            const _exhaustive: never = field.type_id;
            throw new Error(`Unhandled custom field type: ${_exhaustive}`);
          }
        }
      })}
    </>
  );
}
